import { MainViewState } from 'cdx-common';
import React from 'react';
import { useFetcher } from 'react-router-dom';
import { loadMainViewData } from './MainViewLoader';

export interface LocalRouteInfo {
    alias: string;
    state: MainViewState;
    promise?: Promise<void>;
    error?: unknown;
}

export const routeInfos = new Map<string, LocalRouteInfo>();

export const getRouteDataUrl = (path: string): string => {
    const url = new URL(path, window.location.origin);
    return url.pathname + url.search;
};

export const ensureRouteData = (path: string): Promise<void> => {
    const url = getRouteDataUrl(path);
    const routeInfo = routeInfos.get(url);

    if (routeInfo?.state === MainViewState.LOADED) {
        return Promise.resolve();
    }

    if (routeInfo?.state === MainViewState.LOADING && routeInfo.promise) {
        return routeInfo.promise;
    }

    const promise = loadMainViewData(url)
        .then(() => {
            routeInfos.set(url, {
                alias: url,
                state: MainViewState.LOADED,
            });
        })
        .catch(error => {
            routeInfos.set(url, {
                alias: url,
                state: MainViewState.ERROR,
                error,
            });
            throw error;
        });

    routeInfos.set(url, {
        alias: url,
        state: MainViewState.LOADING,
        promise,
    });

    return promise;
};

export const invalidateRouteData = (path: string) => {
    const url = getRouteDataUrl(path);
    const routeInfo = routeInfos.get(url);

    if (routeInfo) {
        routeInfos.set(url, {
            alias: url,
            state: MainViewState.OBSOLETE,
        });
    }
};

export const clearRouteData = () => {
    routeInfos.clear();
};

export const useRoutePreloader = () => {
    const fetcher = useFetcher();

    const loadNavigationData = React.useCallback((path: string): Promise<void> => {
        const url = getRouteDataUrl(path);
        const routeInfo = routeInfos.get(url);

        if (routeInfo?.state === MainViewState.LOADED) {
            return Promise.resolve();
        }

        if (routeInfo?.state === MainViewState.LOADING && routeInfo.promise) {
            return routeInfo.promise;
        }

        return fetcher.load(url);
    }, [fetcher]);

    return {
        loadNavigationData,
        state: fetcher.state,
    };
};
